import { useState, useEffect, useRef } from "react";
import { useGroupedCandidates } from "../../contexts/GroupedCandidatesContext";
import { interviewService } from "../../services/interviewService";

const InterviewForm = ({
  closeModal,
  onSubmit,
  isEditMode = false,
  isScheduling = false,
  mode = "create", // "create" | "edit" | "reschedule" | "scheduleNext"
  initialValues = {
    candidate_id: "",
    scheduled_at: "",
    status: "Scheduled",
    interview_type: "", // Default interview type
    interview_link: "",
    location: "",
    round_type: "",
    interviewers: [],
  },
}) => {
  const { shortlistedCandidates, fetchGroupedCandidates } = useGroupedCandidates(); // Use context for shortlisted candidates only
  
  // Ensure initialValues is always an object
  const safeInitialValues = initialValues || {
    candidate_id: "",
    scheduled_at: "",
    status: "Scheduled",
    interview_type: "",
    interview_link: "",
    location: "",
    round_type: "",
    interviewers: [],
    assigned_to: "",
    campaign_id: "",
  };
  
  const [formData, setFormData] = useState(safeInitialValues);
  const [error, setError] = useState("");
  const [candidateError, setCandidateError] = useState("");
  const [interviewersError, setInterviewersError] = useState("");
  const [headInterviewerError, setHeadInterviewerError] = useState("");
  
  // Interviewer search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedInterviewers, setSelectedInterviewers] = useState(
    safeInitialValues.interviewers || []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const prevInitialValuesIdRef = useRef(undefined);
  const prevRoundIdRef = useRef(undefined);

  // Candidate search states
  const [candidateSearchQuery, setCandidateSearchQuery] = useState("");
  const [candidateSearchResults, setCandidateSearchResults] = useState([]);
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);
  const candidateDropdownRef = useRef(null);
  const hasFetchedCandidatesRef = useRef(false); // Track if we've fetched candidates on first click

  // Debounced search for interviewers
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim().length > 0) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          const response = await interviewService.searchInterviewers(searchQuery);
          if (response.success && response.data) {
            // Log the raw API response to see structure
            console.log('API Search Response - Raw data:', response.data);
            
            // API returns: { employee_id: "10016", full_name: "HOD User" } or { id: 123, employee_id: "EMP001" }
            // Ensure interviewer objects have the correct structure
            const formattedResults = response.data.map((interviewer) => {
              const formatted = {
                ...interviewer,
                // Preserve original id field (numeric) if it exists
                id: interviewer.id,
                // employee_id comes as string from API, keep it but ensure it's available
                employee_id: interviewer.employee_id || interviewer.id || interviewer.user_id,
                full_name: interviewer.full_name || interviewer.name || `${interviewer.first_name || ''} ${interviewer.last_name || ''}`.trim(),
              };
              console.log('Formatted interviewer from search:', formatted);
              return formatted;
            });
            setSearchResults(formattedResults);
            setShowDropdown(true);
          } else {
            setSearchResults([]);
          }
        } catch (error) {
          console.error("Error searching interviewers:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 400); // 400ms debounce
    } else {
      setSearchResults([]);
      setShowDropdown(false);
      setIsSearching(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (candidateDropdownRef.current && !candidateDropdownRef.current.contains(event.target)) {
        setShowCandidateDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter candidates based on search query - only show candidates where interview_scheduled is not "yes"
  useEffect(() => {
    if (candidateSearchQuery.trim().length > 0) {
      const filtered = shortlistedCandidates.filter((candidate) =>
        // Only show candidates that don't have an interview scheduled
        candidate.interview_scheduled !== "yes" &&
        (candidate.name.toLowerCase().includes(candidateSearchQuery.toLowerCase()) ||
        (candidate.email && candidate.email.toLowerCase().includes(candidateSearchQuery.toLowerCase())))
      );
      setCandidateSearchResults(filtered);
      setShowCandidateDropdown(true);
    } else {
      setCandidateSearchResults([]);
      setShowCandidateDropdown(false);
    }
  }, [candidateSearchQuery, shortlistedCandidates]);

  // Update formData when selectedInterviewers changes
  useEffect(() => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        interviewers: selectedInterviewers,
      };

      // Clear assigned_to if the selected head interviewer is removed
      if (prev.assigned_to) {
        const headInterviewerStillExists = selectedInterviewers.some(
          (interviewer) => String(interviewer.employee_id) === String(prev.assigned_to)
        );
        if (!headInterviewerStillExists) {
          updated.assigned_to = "";
        }
      }

      // Auto-assign head interviewer when only one interviewer is selected
      if (selectedInterviewers.length === 1) {
        updated.assigned_to = String(selectedInterviewers[0].employee_id || "");
      }

      // If more than one interviewer and no head selected, default to the first
      if (selectedInterviewers.length > 1 && !updated.assigned_to) {
        updated.assigned_to = String(selectedInterviewers[0].employee_id || "");
      }

      // If no interviewers, ensure head interviewer is cleared
      if (selectedInterviewers.length === 0) {
        updated.assigned_to = "";
      }

      return updated;
    });
  }, [selectedInterviewers]);

  // Update formData when initialValues change (for edit mode and reschedule mode)
  // Only update when initialValues.id or round_id changes to prevent unnecessary resets
  useEffect(() => {
    const currentId = initialValues?.id;
    const currentRoundId = initialValues?.round_id;
    const prevId = prevInitialValuesIdRef.current;
    const prevRoundId = prevRoundIdRef.current;
    
    // Only update if the ID or round_id actually changed (switching between different interviews or edit/create mode)
    // This prevents the form from resetting when initialValues object reference changes but values are the same
    const idChanged = prevId !== currentId;
    const roundIdChanged = prevRoundId !== currentRoundId;
    
    // Also handle initial mount case - if we have initialValues but refs are undefined, initialize
    const isInitialMount = (prevId === undefined && currentId !== undefined) || 
                          (prevRoundId === undefined && currentRoundId !== undefined);
    
    if (idChanged || roundIdChanged || isInitialMount) {
      // Handle reschedule mode (has round_id) or edit mode (has id)
      if (initialValues && (initialValues.id || initialValues.round_id)) {
        // Handle assigned_to from initialValues (could be assigned_to or head_interviewer)
        // Extract employee_id if assigned_to is an object
        let assignedToValue = initialValues.assigned_to || initialValues.head_interviewer || "";
        
        if (assignedToValue) {
          if (typeof assignedToValue === 'object') {
            // Extract employee_id from nested structure
            if (assignedToValue.employee_detail && assignedToValue.employee_detail.employee_id) {
              assignedToValue = assignedToValue.employee_detail.employee_id;
            } else if (assignedToValue.employee_id) {
              assignedToValue = assignedToValue.employee_id;
            } else if (assignedToValue.id) {
              assignedToValue = assignedToValue.id;
            } else {
              assignedToValue = "";
            }
          }
        }
        
        // Convert to string for consistency with form select value
        const assignedToStr = assignedToValue ? String(assignedToValue) : "";
        
        setFormData({
          ...initialValues,
          assigned_to: assignedToStr,
        });
        
        // Handle interviewers - they might be objects or IDs
        // Also handle nested employee_detail structure from assignments
        const interviewers = (initialValues.interviewers || []).map((interviewer) => {
          if (typeof interviewer === 'object') {
            // Extract employee_id from nested structure if available
            let employeeId = "";
            if (interviewer.employee_detail && interviewer.employee_detail.employee_id) {
              employeeId = String(interviewer.employee_detail.employee_id);
            } else if (interviewer.employee_id) {
              employeeId = String(interviewer.employee_id);
            } else if (interviewer.id) {
              employeeId = String(interviewer.id);
            } else if (interviewer.user_id) {
              employeeId = String(interviewer.user_id);
            }
            
            // Extract full_name from nested structure if available
            const fullName = interviewer.full_name || 
                           interviewer.name || 
                           (interviewer.employee_detail && interviewer.employee_detail.full_name) ||
                           `${interviewer.first_name || ''} ${interviewer.last_name || ''}`.trim() || 
                           `Interviewer ${employeeId}`;
            
            return {
              ...interviewer,
              employee_id: employeeId,
              full_name: fullName,
            };
          }
          // If it's just an ID, create a minimal object structure
          const id = String(interviewer);
          return {
            employee_id: id,
            id: id,
            full_name: `Interviewer ${id}`,
          };
        });
        setSelectedInterviewers(interviewers);
      } else if (prevId !== undefined && currentId === undefined && prevRoundId !== undefined && currentRoundId === undefined) {
        // Switching from edit/reschedule mode to create mode - reset form
        setFormData({
          candidate_id: "",
          scheduled_at: "",
          status: "Scheduled",
          interview_type: "",
          interview_link: "",
          location: "",
          round_type: "",
          interviewers: [],
          assigned_to: "",
          campaign_id: "",
        });
        setSelectedInterviewers([]);
      }
      // Update the refs to track the current IDs
      prevInitialValuesIdRef.current = currentId;
      prevRoundIdRef.current = currentRoundId;
    }
  }, [initialValues?.id, initialValues?.round_id, mode]);

  // Initialize candidate search query when initialValues has candidate_id
  useEffect(() => {
    if (initialValues?.candidate_id && shortlistedCandidates.length > 0) {
      const candidate = shortlistedCandidates.find(
        (c) => c.id === initialValues.candidate_id
      );
      if (candidate) {
        setCandidateSearchQuery(candidate.name);
        // Extract campaign_id from candidate
        if (candidate.campaign_id) {
          setFormData((prev) => ({ ...prev, campaign_id: candidate.campaign_id }));
        }
      }
    }
  }, [initialValues?.candidate_id, shortlistedCandidates]);

  const handleInterviewerSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSelectInterviewer = (interviewer) => {
    // Log the original interviewer object to debug structure
    console.log('Selecting interviewer - Original object:', interviewer);
    
    // API returns employee_id as string, normalize it
    // Preserve both id (numeric) and employee_id (string) for proper extraction
    const normalizedInterviewer = {
      ...interviewer,
      // Preserve ALL original fields, especially numeric id
      id: interviewer.id,
      // Also check for other possible ID fields
      user_id: interviewer.user_id,
      // employee_id comes as string from API (e.g., "10016" or "EMP001"), keep it consistent
      employee_id: String(interviewer.employee_id || interviewer.id || interviewer.user_id),
      full_name: interviewer.full_name || interviewer.name || `${interviewer.first_name || ''} ${interviewer.last_name || ''}`.trim(),
    };

    console.log('Selecting interviewer - Normalized object:', normalizedInterviewer);

    // Check if already selected (compare as strings)
    const isAlreadySelected = selectedInterviewers.some(
      (selected) => String(selected.employee_id) === normalizedInterviewer.employee_id
    );

    // Check if max limit reached
    if (selectedInterviewers.length >= 5) {
      return;
    }

    if (!isAlreadySelected) {
      setSelectedInterviewers((prev) => [...prev, normalizedInterviewer]);
      // Keep dropdown open so user can continue adding more interviewers
      // Keep search query so user can continue searching and adding more
    }
  };

  const handleRemoveInterviewer = (employeeId) => {
    setSelectedInterviewers((prev) =>
      prev.filter((interviewer) => String(interviewer.employee_id) !== String(employeeId))
    );
  };

  const handleCandidateSearch = (e) => {
    setCandidateSearchQuery(e.target.value);
  };

  const handleSelectCandidate = (candidate) => {
    if (!candidate.campaign_id) {
      setCandidateError("Selected candidate does not have a campaign assigned. Please select a different candidate.");
      return;
    }
    
    // Extract first round type from round_type_mapping
    let firstRoundType = "";
    if (candidate.round_type_mapping && typeof candidate.round_type_mapping === 'object') {
      const roundTypes = Object.values(candidate.round_type_mapping);
      if (roundTypes.length > 0) {
        firstRoundType = roundTypes[0];
      }
    }
    
    setFormData((prev) => ({ 
      ...prev, 
      candidate_id: candidate.id,
      campaign_id: candidate.campaign_id || "",
      round_type: firstRoundType,
    }));
    setCandidateSearchQuery(candidate.name);
    setShowCandidateDropdown(false);
    setCandidateError(""); // Clear error when candidate is selected
  };


  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "interview_link" && value === "generate") {
      const generateRandomString = (length) => {
        const characters =
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i = 0; i < length; i++) {
          result += characters.charAt(
            Math.floor(Math.random() * characters.length)
          );
        }
        return result;
      };

      const randomString = generateRandomString(8); // Generate a random string of length 8
      const autogeneratedLink = `https://teamtime.live/room/${randomString}`;

      setFormData((prev) => ({ ...prev, interview_link: autogeneratedLink }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "scheduled_at") setError(""); // Clear error when user changes date
    if (name === "assigned_to") setHeadInterviewerError(""); // Clear error when head interviewer is selected
  };

  const isValidDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set today's time to 00:00:00

    const inputDate = new Date(dateString);

    return inputDate >= today;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Only validate candidate_id for new interviews (create mode)
    if (!isEditMode && mode === "create" && !formData.candidate_id) {
      setCandidateError("Please select a candidate.");
      setError("");
      setInterviewersError("");
      setHeadInterviewerError("");
      return;
    }

    // Validate interviewers are selected
    if (!selectedInterviewers || selectedInterviewers.length === 0) {
      setInterviewersError("Please select at least one interviewer.");
      setError("");
      setCandidateError("");
      setHeadInterviewerError("");
      return;
    }

    // Validate head interviewer only when multiple interviewers are present
    if (selectedInterviewers.length > 1 && !formData.assigned_to) {
      setHeadInterviewerError("Please select a head interviewer.");
      setError("");
      setCandidateError("");
      setInterviewersError("");
      return;
    }

    if (!isValidDate(formData.scheduled_at)) {
      setError("Scheduled date must be today or later.");
      setCandidateError("");
      setInterviewersError("");
      setHeadInterviewerError("");
      return;
    }

    setError("");
    setCandidateError("");
    setInterviewersError("");
    setHeadInterviewerError("");

    // Transform formData to match API format
    const formatDateTime = (dateTimeString) => {
      if (!dateTimeString) return null;
      // Convert datetime-local format (YYYY-MM-DDTHH:mm) to API format (YYYY-MM-DD HH:mm:ss)
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Format data based on edit mode
    // Helper to extract interviewer ID - handles both numeric and string IDs
    const getInterviewerId = (interviewer) => {
      if (!interviewer) {
        console.warn('getInterviewerId: interviewer is null/undefined');
        return null;
      }
      
      if (typeof interviewer === 'string' || typeof interviewer === 'number') {
        // If it's already a number, return it
        if (typeof interviewer === 'number') {
          return interviewer;
        }
        // Try to parse as integer
        const parsed = parseInt(interviewer);
        if (!isNaN(parsed)) {
          return parsed;
        }
        // If parsing fails, it might be a string ID like "EMP001" - return as-is
        console.warn('getInterviewerId: Non-numeric ID, using as string:', interviewer);
        return interviewer;
      }
      
      if (typeof interviewer === 'object') {
        // Try all possible ID fields in priority order
        const possibleIds = [
          interviewer.id,
          interviewer.user_id,
          interviewer.employee_id,
          interviewer.employee_detail?.employee_id,
          interviewer.employee_detail?.id,
        ].filter(id => id !== undefined && id !== null && id !== '');
        
        // First, try to find a numeric ID
        for (const id of possibleIds) {
          if (typeof id === 'number' && id > 0) {
            return id;
          }
          const parsedId = parseInt(String(id));
          if (!isNaN(parsedId) && parsedId > 0) {
            return parsedId;
          }
        }
        
        // If no numeric ID found, use the first available string ID (e.g., "EMP001")
        // The API might accept string IDs
        for (const id of possibleIds) {
          if (id !== undefined && id !== null && id !== '') {
            // Return as string if it's not numeric
            const stringId = String(id);
            console.log('getInterviewerId: Using string ID:', stringId);
            return stringId;
          }
        }
        
        // If still no ID found, log error
        console.error('getInterviewerId: No ID found in interviewer object:', {
          id: interviewer.id,
          user_id: interviewer.user_id,
          employee_id: interviewer.employee_id,
          employee_detail: interviewer.employee_detail,
          allKeys: Object.keys(interviewer),
          fullObject: JSON.stringify(interviewer, null, 2)
        });
        return null;
      }
      
      console.warn('getInterviewerId: Unexpected interviewer type:', typeof interviewer, interviewer);
      return null;
    };

    // Format data based on mode
    // Use selectedInterviewers as primary source, fallback to formData.interviewers if needed
    // This ensures we capture the data even if there's a timing issue with state updates
    const interviewersToProcess = selectedInterviewers.length > 0 
      ? selectedInterviewers 
      : (formData.interviewers || []);
    
    const interviewerIds = interviewersToProcess
      .map(getInterviewerId)
      .filter(id => id !== null && id !== undefined && id !== '');
    
    // Debug: Log what we're processing
    console.log('Processing interviewers:', {
      selectedInterviewers,
      formDataInterviewers: formData.interviewers,
      interviewersToProcess,
      extractedIds: interviewerIds,
      formDataAssignedTo: formData.assigned_to
    });
    
    // Final validation: Ensure we have interviewer IDs
    if (interviewerIds.length === 0) {
      console.error('No valid interviewer IDs extracted!', {
        selectedInterviewers: selectedInterviewers.map(i => ({
          id: i.id,
          employee_id: i.employee_id,
          user_id: i.user_id,
          fullObject: i
        })),
        formDataInterviewers: formData.interviewers,
        interviewersToProcess: interviewersToProcess.map(i => ({
          id: i?.id,
          employee_id: i?.employee_id,
          user_id: i?.user_id,
          fullObject: i
        }))
      });
      setInterviewersError("Failed to extract interviewer IDs. Please check the console for details about the interviewer data structure.");
      return;
    }
    
    // Ensure assigned_to is properly extracted and matches one of the interviewer IDs
    // Handle both numeric and string IDs
    let assignedToId = null;
    if (formData.assigned_to) {
      const assignedToValue = String(formData.assigned_to);
      // Check if it matches any interviewer ID (numeric or string)
      if (interviewerIds.includes(assignedToValue) || interviewerIds.includes(parseInt(assignedToValue))) {
        // Use the actual ID from interviewerIds (could be string or number)
        const matchingId = interviewerIds.find(id => 
          String(id) === assignedToValue || id === parseInt(assignedToValue)
        );
        assignedToId = matchingId || assignedToValue;
      } else if (interviewerIds.length > 0) {
        // If assigned_to doesn't match, use the first interviewer as fallback
        console.warn('assigned_to does not match any selected interviewer, using first interviewer');
        assignedToId = interviewerIds[0];
      }
    } else if (interviewerIds.length === 1) {
      // Auto-assign if only one interviewer
      assignedToId = interviewerIds[0];
    } else if (interviewerIds.length > 1) {
      // Default to first interviewer if multiple and none selected
      assignedToId = interviewerIds[0];
    }
    
    // Helper to convert empty strings to null for optional fields
    const toNullIfEmpty = (value) => {
      if (value === "" || value === null || value === undefined) return null;
      return value;
    };
    
    let transformedData;
    
    if (mode === "reschedule") {
      // For reschedule mode
      transformedData = {
        interviewers: interviewerIds,
        assigned_to: assignedToId,
        scheduled_at: formatDateTime(formData.scheduled_at),
        interview_type: formData.interview_type || null,
        interview_link: toNullIfEmpty(formData.interview_link),
        location: toNullIfEmpty(formData.location),
        round_type: formData.round_type || null,
      };
    } else if (mode === "scheduleNext") {
      // For schedule next round mode
      transformedData = {
        round_number: initialValues?.next_round_number || ((initialValues?.current_round || 0) + 1),
        assigned_to: assignedToId,
        interview_type: formData.interview_type || null,
        interview_link: toNullIfEmpty(formData.interview_link),
        location: toNullIfEmpty(formData.location),
        scheduled_at: formatDateTime(formData.scheduled_at),
        round_type: formData.round_type || null,
        interviewers: interviewerIds,
      };
    } else if (isEditMode) {
      // For edit mode
      transformedData = {
        interviewers: interviewerIds,
        assigned_to: assignedToId,
        scheduled_at: formatDateTime(formData.scheduled_at),
        interview_type: formData.interview_type || null,
        interview_link: toNullIfEmpty(formData.interview_link),
        location: toNullIfEmpty(formData.location),
        round_type: formData.round_type || null,
      };
    } else {
      // For create mode
      transformedData = {
        candidate_id: parseInt(formData.candidate_id),
        campaign_id: formData.campaign_id ? parseInt(formData.campaign_id) : null,
        assigned_to: assignedToId,
        interview_type: formData.interview_type || null,
        interview_link: toNullIfEmpty(formData.interview_link),
        location: toNullIfEmpty(formData.location),
        scheduled_at: formatDateTime(formData.scheduled_at),
        round_type: formData.round_type || null,
        interviewers: interviewerIds,
      };
    }

    // Debug log to verify payload structure
    console.log('Interview Form Payload:', transformedData);
    console.log('Selected Interviewers:', selectedInterviewers);
    console.log('Interviewer IDs:', interviewerIds);
    console.log('Assigned To ID:', assignedToId);
    
    onSubmit(transformedData);
    // Don't close modal here - let parent handle it after API success
  };

  // Get selected candidate name for display
  const selectedCandidate = shortlistedCandidates.find(
    (c) => c.id === formData.candidate_id
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-poppins">
      {isScheduling && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3 mb-4">
          <p className="text-blue-800 dark:text-blue-200 text-sm font-medium">
            Scheduling interview...
          </p>
        </div>
      )}
      {/* Show candidate name in edit, reschedule, and scheduleNext modes (read-only) */}
      {(isEditMode || mode === "reschedule" || mode === "scheduleNext") && (
        <div>
          <label
            htmlFor="candidate_name_display"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Candidate
          </label>
          <input
            type="text"
            id="candidate_name_display"
            value={initialValues?.candidate?.name || "Unknown Candidate"}
            readOnly
            disabled
            className="mt-1 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
          />
        </div>
      )}

      {/* Only show candidate selection for new interviews (create mode) */}
      {!isEditMode && mode === "create" && (
        <div className="relative" ref={candidateDropdownRef}>
          <label
            htmlFor="candidate_search"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Candidate <span className="text-red-500 ml-1">*</span>
          </label>
        <input
          type="text"
          id="candidate_search"
          name="candidate_search"
          value={candidateSearchQuery || selectedCandidate?.name || ""}
          onChange={handleCandidateSearch}
          onFocus={async () => {
            // Fetch candidates on first click
            if (!hasFetchedCandidatesRef.current) {
              hasFetchedCandidatesRef.current = true;
              await fetchGroupedCandidates();
            }
            if (candidateSearchQuery || shortlistedCandidates.length > 0) {
              setShowCandidateDropdown(true);
            }
          }}
          disabled={isScheduling}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border rounded-md ${
            candidateError ? "border-red-500" : "border-gray-300"
          } ${isScheduling ? "opacity-50 cursor-not-allowed" : ""}`}
          placeholder="Search candidate by name or email"
        />
        <input
          type="hidden"
          name="candidate_id"
          value={formData.candidate_id || ""}
          required
        />
        {candidateError && (
          <p className="text-red-500 text-sm mt-1">{candidateError}</p>
        )}

        {/* Candidate Search Results Dropdown */}
        {showCandidateDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
            {candidateSearchResults.length > 0 ? (
              <ul className="py-1">
                {candidateSearchResults.map((candidate) => (
                  <li
                    key={candidate.id}
                    onClick={() => handleSelectCandidate(candidate)}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {candidate.name}
                      </span>
                      {candidate.email && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {candidate.email}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : candidateSearchQuery.trim().length > 0 ? (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                No candidates found
              </div>
            ) : shortlistedCandidates.filter(c => c.interview_scheduled !== "yes").length > 0 ? (
              <ul className="py-1">
                {shortlistedCandidates
                  .filter(candidate => candidate.interview_scheduled !== "yes")
                  .map((candidate) => (
                  <li
                    key={candidate.id}
                    onClick={() => handleSelectCandidate(candidate)}
                    className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {candidate.name}
                      </span>
                      {candidate.email && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {candidate.email}
                        </span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                No candidates available
              </div>
            )}
          </div>
        )}
        </div>
      )}

      <div>
        <label
          htmlFor="round_type"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Round Type <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          name="round_type"
          id="round_type"
          value={formData.round_type}
          onChange={handleChange}
          disabled={isScheduling || !!formData.candidate_id || mode === "reschedule" || mode === "scheduleNext"}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md ${
            (isScheduling || formData.candidate_id || mode === "reschedule" || mode === "scheduleNext") ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800" : ""
          }`}
          required
        >
          <option value="">Select Round Type</option>
          <option value="Screening">Screening</option>
          <option value="Technical">Technical</option>
          <option value="HR">HR</option>
          <option value="Managerial">Managerial</option>
          <option value="Final">Final</option>
        </select>
      </div>

      <div>
        <label
          htmlFor="interview_type"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Interview Type <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          name="interview_type"
          id="interview_type"
          value={formData.interview_type}
          onChange={handleChange}
          disabled={isScheduling}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md ${
            isScheduling ? "opacity-50 cursor-not-allowed" : ""
          }`}
          required
        >
          <option value="">Select Type</option>
          <option value="virtual">Virtual</option>
          <option value="physical">Physical</option>
        </select>
      </div>

      {formData.interview_type === "virtual" && (
        <div>
          <label
            htmlFor="interview_link"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Interview Link
          </label>
          <div className="flex items-center gap-4">
            <input
              type="text"
              name="interview_link"
              id="interview_link"
              value={formData.interview_link}
              onChange={handleChange}
              disabled={isScheduling}
              className={`w-full block p-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-md ${
                isScheduling ? "opacity-50 cursor-not-allowed" : ""
              }`}
              placeholder="Enter or generate interview link"
            />
            <button
              type="button"
              onClick={() =>
                handleChange({
                  target: {
                    name: "interview_link",
                    value: "generate",
                  },
                })
              }
              disabled={isScheduling}
              className={`w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
                isScheduling ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              Generate Link
            </button>
          </div>
        </div>
      )}

      {formData.interview_type === "physical" && (
        <div>
          <label
            htmlFor="location"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Location

            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            name="location"
            id="location"
            value={formData.location}
            onChange={handleChange}
            disabled={isScheduling}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md ${
              isScheduling ? "opacity-50 cursor-not-allowed" : ""
            }`}
            placeholder="Enter physical interview location"
            required
          />
        </div>
      )}

       <div className="relative" ref={dropdownRef}>
         <label
           htmlFor="interviewer_search"
           className="block text-sm font-medium text-gray-700 dark:text-white"
         >
           Interviewers <span className="text-red-500 ml-1">*</span>
         </label>
         <input
           type="text"
           id="interviewer_search"
           value={searchQuery}
           onChange={handleInterviewerSearch}
           onFocus={() => {
             if (searchQuery || searchResults.length > 0) {
               setShowDropdown(true);
             }
           }}
           disabled={isScheduling || selectedInterviewers.length >= 5}
           className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border rounded-md ${
             interviewersError ? "border-red-500" : "border-gray-300"
           } ${isScheduling ? "opacity-50 cursor-not-allowed" : ""}`}
           placeholder="Search interviewers (comma-separated for multiple)"
         />
         {interviewersError && (
           <p className="text-red-500 text-sm mt-1">{interviewersError}</p>
         )}
         {selectedInterviewers.length >= 5 && (
           <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
             Maximum 5 interviewers can be added
           </p>
         )}

         {/* Selected Interviewers Display - Between input and dropdown */}
         {selectedInterviewers.length > 0 && (
           <div className="mt-2 flex flex-wrap gap-2">
             {selectedInterviewers.map((interviewer) => (
               <div
                 key={interviewer.employee_id}
                 className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-md text-sm"
               >
                 <span>
                   {interviewer.full_name} ({interviewer.employee_id})
                 </span>
                 <button
                   type="button"
                   onClick={() =>
                     handleRemoveInterviewer(interviewer.employee_id)
                   }
                   className="text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100 font-bold ml-1"
                   aria-label="Remove interviewer"
                 >
                   ×
                 </button>
               </div>
             ))}
           </div>
         )}

        {/* Search Results Dropdown */}
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
            {isSearching ? (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                Searching...
              </div>
            ) : searchResults.length > 0 ? (
              <ul className="py-1">
                {searchResults.map((interviewer) => {
                  const isSelected = selectedInterviewers.some(
                    (selected) =>
                      String(selected.employee_id) === String(interviewer.employee_id)
                  );
                  const isDisabled =
                    isSelected || selectedInterviewers.length >= 5;

                  return (
                    <li
                      key={interviewer.employee_id}
                      onClick={() =>
                        !isDisabled && handleSelectInterviewer(interviewer)
                      }
                      className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {interviewer.full_name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {interviewer.employee_id}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : searchQuery.trim().length > 0 ? (
              <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                No interviewers found
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Head Interviewer Field - only when more than one interviewer is selected */}
      {selectedInterviewers.length > 1 && (
        <div>
          <label
            htmlFor="assigned_to"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Head Interviewer <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            name="assigned_to"
            id="assigned_to"
            value={formData.assigned_to || ""}
            onChange={handleChange}
            disabled={isScheduling}
            className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border rounded-md ${
              headInterviewerError ? "border-red-500" : "border-gray-300"
            } ${isScheduling ? "opacity-50 cursor-not-allowed" : ""}`}
            required
          >
            <option value="">Select Head Interviewer</option>
            {selectedInterviewers.map((interviewer) => (
              <option key={interviewer.employee_id} value={String(interviewer.employee_id)}>
                {interviewer.full_name} ({interviewer.employee_id})
              </option>
            ))}
          </select>
          {headInterviewerError && (
            <p className="text-red-500 text-sm mt-1">{headInterviewerError}</p>
          )}
        </div>
      )}

      <div>
        <label
          htmlFor="scheduled_at"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Scheduled At <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="datetime-local"
          name="scheduled_at"
          id="scheduled_at"
          value={formData.scheduled_at}
          onChange={handleChange}
          disabled={isScheduling}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border rounded-md ${
            error ? "border-red-500" : "border-gray-300"
          } ${isScheduling ? "opacity-50 cursor-not-allowed" : ""}`}
          required
        />
        {error && (
          <p className="text-red-500 text-sm mt-1">{error}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 items-center">
        <button
          type="button"
          onClick={closeModal}
          disabled={isScheduling}
          className={`px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md ${
            isScheduling ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isScheduling}
          className={`px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 ${
            isScheduling ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isScheduling 
            ? (mode === "reschedule" ? "Rescheduling..." : mode === "scheduleNext" ? "Scheduling..." : "Scheduling...")
            : mode === "reschedule"
              ? "Reschedule Round"
              : mode === "scheduleNext"
                ? "Schedule Next Round"
                : initialValues?.id 
                  ? "Update Interview" 
                  : "Schedule Interview"}
        </button>
      </div>
    </form>
  );
};

export default InterviewForm;
